import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Alert, AlertTitle, CircularProgress, TextField, Button, Chip, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Collapse, IconButton, TablePagination, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { telegrafFleetAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };
const GROUPS = [{ key: 'app', label: 'Application servers', tag: 'Sumo = Yes' }, { key: 'db', label: 'Database servers', tag: 'Sumo = Yes-Database' }];
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmt = (t) => (t ? new Date(t).toLocaleString() : '-');
const STATUS = { matches: 'success', differs: 'warning', 'not checked': 'default', unreachable: 'default', error: 'error', 'not installed': 'default' };
const PER = 25;

function Diff({ text }) {
  return (
    <Box component="pre" sx={{ m: 0, p: 1.5, maxHeight: 320, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, fontSize: '0.78rem' }}>
      {text.split('\n').map((l, i) => (
        <div key={i} style={{ color: l.startsWith('+') && !l.startsWith('+++') ? '#2e7d32' : l.startsWith('-') && !l.startsWith('---') ? '#c62828' : undefined }}>{l || ' '}</div>
      ))}
    </Box>
  );
}

function ResultsTable({ results }) {
  return (
    <TableContainer sx={{ maxHeight: 360 }}>
      <Table size="small" stickyHeader>
        <TableHead><TableRow><TableCell>Server</TableCell><TableCell>Environment</TableCell><TableCell>Result</TableCell><TableCell>Backup of the old file</TableCell></TableRow></TableHead>
        <TableBody>
          {(results || []).map((r) => (
            <TableRow key={r.instance_id}>
              <TableCell sx={{ fontFamily: 'monospace' }}>{r.name}</TableCell>
              <TableCell>{r.environment}</TableCell>
              <TableCell>
                {r.ok && r.unchanged && <Chip size="small" label="Already up to date" />}
                {r.ok && !r.unchanged && r.installed !== false && <Chip size="small" color="success" label="Updated" />}
                {r.ok && r.installed === false && <Chip size="small" label="Telegraf not installed" />}
                {!r.ok && r.skipped && <Chip size="small" label="Not attempted" />}
                {!r.ok && !r.skipped && <Chip size="small" color={r.rolled_back ? 'warning' : 'error'} label={r.rolled_back ? 'Rolled back' : 'Failed'} />}
                {r.error && <Typography variant="caption" color="error" display="block">{r.error}</Typography>}
              </TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.backup || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ReviewDialog({ group, master, text, onClose, onSaved }) {
  const [check, setCheck] = useState(null);
  const [reason, setReason] = useState('');
  const [ticket, setTicket] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    telegrafFleetAPI.checkEdit(group.key, { text, base_version: master.version_id }).then((r) => setCheck(r.data)).catch((err) => setError(errText(err, 'Could not check the change')));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true); setError(null);
    try { setSaved((await telegrafFleetAPI.saveMaster(group.key, { text, base_version: master.version_id, reason, ticket: ticket || null })).data); onSaved(); }
    catch (err) { setError(errText(err, 'Could not save the change')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Change the default for {group.label.toLowerCase()}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!check && !error && <CircularProgress size={22} />}
        {check && !saved && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>{check.added} line(s) added and {check.removed} removed. Telegraf checks the file on each server before it replaces the old one, but the file is only sent when you roll it out.</Typography>
            <Diff text={check.diff} />
            <Alert severity="info" sx={{ my: 2 }}>
              The old default is copied to the Archive folder in S3 first (S3 also keeps every version). New installs get this file straight away; servers that already exist only change when you roll it out below.
            </Alert>
            <TextField fullWidth size="small" label="Why is this change needed?" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mb: 2 }} helperText="At least 5 characters. Kept in the audit trail." />
            <TextField size="small" label="Ticket number (optional)" value={ticket} onChange={(e) => setTicket(e.target.value)} />
          </>
        )}
        {saved && <Alert severity="success">Saved. The new default is in S3 and the previous one is kept as {saved.detail?.archive_key?.split('/').pop()}. Roll it out to the servers when you are ready.</Alert>}
      </DialogContent>
      <DialogActions>
        {!saved && <Button onClick={onClose}>Cancel</Button>}
        {!saved && <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={save} disabled={busy || !check || reason.trim().length < 5}>Save as the new default</Button>}
        {saved && <Button variant="contained" onClick={onClose}>Close</Button>}
      </DialogActions>
    </Dialog>
  );
}

function RolloutDialog({ group, wave, counts, masterSha, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [ticket, setTicket] = useState('');
  const [error, setError] = useState(null);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const poll = useCallback((id) => {
    telegrafFleetAPI.rollout(id).then((r) => {
      setRun(r.data);
      if (r.data.status === 'running') timer.current = setTimeout(() => poll(id), 3000); else onDone();
    }).catch(() => { timer.current = setTimeout(() => poll(id), 5000); });
  }, [onDone]);

  const start = async () => {
    setBusy(true); setError(null);
    try {
      const r = await telegrafFleetAPI.startRollout(group.key, { wave, expected_sha: masterSha, reason, ticket: ticket || null, only_differing: true });
      setRun({ status: 'running', total: r.data.total, done: 0, failed: 0, results: [] }); poll(r.data.id);
    } catch (err) { setError(errText(err, 'Could not start the rollout')); }
    finally { setBusy(false); }
  };

  const running = run?.status === 'running';
  const finished = run && !running;
  return (
    <Dialog open onClose={running ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Roll out the default to the {wave} {group.label.toLowerCase()}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!run && (
          <>
            <Typography variant="body2" sx={{ mb: 1.5 }}>{counts.needs} of {counts.total} {wave} servers need the current default. Servers already known to match are skipped.</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Each server downloads the default itself and checks it is exactly the version you reviewed. It then copies its old file beside it as <b>telegraf.conf_date_time_backup</b>, has the new file checked by its own Telegraf, replaces it and restarts the Telegraf service (a few seconds without metrics from that server). If the service does not stay running the old file is put back. Servers are updated a few at a time and the rollout stops after 3 failures.
            </Alert>
            <TextField fullWidth size="small" label="Why is this rollout needed?" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mb: 2 }} helperText="At least 5 characters. Kept in the audit trail." />
            <TextField size="small" label="Ticket number (optional)" value={ticket} onChange={(e) => setTicket(e.target.value)} />
          </>
        )}
        {run && (
          <>
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={Math.min(100, (run.done / Math.max(run.total, 1)) * 100)} />
              <Typography variant="body2" sx={{ mt: 0.5 }}>{run.done} of {run.total} servers done{run.failed ? `, ${run.failed} failed` : ''}{running ? '. This can take several minutes. Do not close this page.' : ''}</Typography>
            </Box>
            {finished && <Alert severity={run.status === 'succeeded' ? 'success' : 'error'} sx={{ mb: 2 }}>{run.status === 'succeeded' ? 'Every server in the wave is on the default.' : (run.error || 'The rollout did not complete.')}</Alert>}
            <ResultsTable results={run.results} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!run && <Button onClick={onClose}>Cancel</Button>}
        {!run && <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={start} disabled={busy || reason.trim().length < 5}>Roll out to {counts.needs} servers</Button>}
        {finished && <Button variant="contained" onClick={onClose}>Close</Button>}
      </DialogActions>
    </Dialog>
  );
}

function History({ group, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [full, setFull] = useState({});
  useEffect(() => { telegrafFleetAPI.history(group.key).then((r) => setData(r.data)).catch(() => setData({ changes: [], rollouts: [] })); }, [group.key, refreshKey]);
  const show = async (id) => {
    setOpen(open === id ? null : id);
    if (!full[id]) { try { const r = await telegrafFleetAPI.rollout(id); setFull((cur) => ({ ...cur, [id]: r.data })); } catch (e) { /* shown empty */ } }
  };
  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>Recent changes and rollouts</Typography>
      {data === null ? <CircularProgress size={20} /> : (
        <>
          <Typography variant="subtitle2">Changes to the default</Typography>
          {data.changes.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>None made from the portal yet.</Typography>}
          {data.changes.map((c) => (
            <Typography key={c.id} variant="body2">{fmt(c.requested_at)}: {c.requested_name || c.requested_by} ({c.reason}{c.ticket ? `, ${c.ticket}` : ''}) <Chip size="small" color={c.status === 'succeeded' ? 'success' : c.status === 'failed' ? 'error' : 'default'} label={c.status} />{c.detail && c.detail.added !== undefined ? ` +${c.detail.added} -${c.detail.removed}` : ''}</Typography>
          ))}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Rollouts</Typography>
          {data.rollouts.length === 0 ? <Typography variant="body2" color="text.secondary">None yet.</Typography> : (
            <Table size="small">
              <TableHead><TableRow><TableCell padding="checkbox" /><TableCell>When</TableCell><TableCell>Wave</TableCell><TableCell>Who</TableCell><TableCell>Why</TableCell><TableCell>Servers</TableCell><TableCell>Result</TableCell></TableRow></TableHead>
              <TableBody>
                {data.rollouts.map((r) => (
                  <React.Fragment key={r.id}>
                    <TableRow hover>
                      <TableCell padding="checkbox"><IconButton size="small" aria-label={`Show results of rollout ${r.id}`} onClick={() => show(r.id)}>{open === r.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></TableCell>
                      <TableCell>{fmt(r.requested_at)}</TableCell><TableCell>{r.wave}</TableCell><TableCell>{r.requested_name || r.requested_by}</TableCell>
                      <TableCell>{r.reason}{r.ticket ? ` (${r.ticket})` : ''}</TableCell><TableCell>{r.done}/{r.total}{r.failed ? `, ${r.failed} failed` : ''}</TableCell>
                      <TableCell><Chip size="small" color={r.status === 'succeeded' ? 'success' : r.status === 'failed' ? 'error' : 'default'} label={r.status} /></TableCell>
                    </TableRow>
                    <TableRow><TableCell sx={{ py: 0 }} colSpan={7}>
                      <Collapse in={open === r.id} unmountOnExit><Box sx={{ py: 1 }}>{r.error && <Typography variant="body2" color="error">{r.error}</Typography>}<ResultsTable results={full[r.id]?.results} /></Box></Collapse>
                    </TableCell></TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </Paper>
  );
}

function Group({ group }) {
  const [master, setMaster] = useState(null);
  const [text, setText] = useState('');
  const [loaded, setLoaded] = useState(null);
  const [fleet, setFleet] = useState(null);
  const [error, setError] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [rolling, setRolling] = useState(null);
  const [envFilter, setEnvFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [checking, setChecking] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const loadMaster = useCallback(async () => {
    setError(null);
    try { const r = (await telegrafFleetAPI.master(group.key)).data; setMaster(r); setText(r.text); setLoaded(null); }
    catch (err) { setError(errText(err, 'Could not read the default config')); }
  }, [group.key]);
  const loadFleet = useCallback(async (refresh = false) => {
    try { setFleet((await telegrafFleetAPI.servers(group.key, refresh)).data); }
    catch (err) { setError(errText(err, 'Could not list the servers')); }
  }, [group.key]);
  useEffect(() => { loadMaster(); loadFleet(); }, [loadMaster, loadFleet]);

  const changed = master && text !== master.text;
  const bump = () => { setRefreshKey((k) => k + 1); loadMaster(); loadFleet(); };

  const loadVersion = async (v) => {
    try { const r = (await telegrafFleetAPI.version(group.key, v.version_id)).data; setText(r.text); setLoaded(r); }
    catch (err) { setError(errText(err, 'Could not read that version')); }
  };

  const runCheck = async () => {
    setChecking(true); setError(null);
    const started = Date.now();
    try {
      await telegrafFleetAPI.checkServers(group.key, { wave: envFilter || null });
      const tick = async (n) => {
        await loadFleet();
        if (n > 40) { setChecking(false); return; }
        timer.current = setTimeout(() => tick(n + 1), 5000);
      };
      timer.current = setTimeout(() => tick(1), 6000);
      setTimeout(() => setChecking(false), 90000);
    } catch (err) { setError(errText(err, 'Could not start the check')); setChecking(false); }
    return started;
  };

  const rows = useMemo(() => (fleet?.servers || []).filter((s) => (!envFilter || s.environment === envFilter) && (!statusFilter || s.status === statusFilter)
    && (!query.trim() || s.name.toLowerCase().includes(query.trim().toLowerCase()))), [fleet, envFilter, statusFilter, query]);
  useEffect(() => { setPage(0); }, [envFilter, statusFilter, query]);
  const tally = useMemo(() => (fleet?.servers || []).reduce((c, s) => ({ ...c, [s.status]: (c[s.status] || 0) + 1 }), {}), [fleet]);

  if (!master && !error) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {master && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Default config for {group.label.toLowerCase()}</Typography>
            <Chip size="small" label={`Last changed ${fmt(master.last_modified)}`} />
            <Chip size="small" variant="outlined" label={`${(master.size / 1024).toFixed(1)} KB`} />
            <Chip size="small" variant="outlined" label={`fingerprint ${master.sha.slice(0, 10)}`} />
          </Box>
          <Alert severity="info" sx={{ mb: 1 }}>
            This is the file new servers get at install and the file you roll out below. The Sumo collector URL is a secret: the portal keeps it, shows a placeholder and puts it back when you save.
            Commands (exec) and other outputs are not allowed.
          </Alert>
          {loaded && <Alert severity="warning" sx={{ mb: 1 }}>Showing the version from {fmt(loaded.last_modified)}. Review and save it to make it the default again.</Alert>}
          <TextField fullWidth multiline minRows={14} maxRows={28} value={text} onChange={(e) => setText(e.target.value)}
            slotProps={{ htmlInput: { spellCheck: false, 'aria-label': 'Default Telegraf config', style: { fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre' } } }} />
          <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setReviewing(true)} disabled={!changed}>Review and save as the new default</Button>
            <Button startIcon={<RefreshIcon />} onClick={loadMaster}>Reload from S3</Button>
            {changed && <Chip color="warning" label="Unsaved changes" />}
          </Box>
          <Accordion disableGutters sx={{ mt: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Earlier versions ({master.versions.length})</Typography></AccordionSummary>
            <AccordionDetails>
              {master.versions.map((v) => (
                <Box key={v.version_id} sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 0.5 }}>
                  <Typography variant="body2" sx={{ minWidth: 190 }}>{fmt(v.last_modified)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>{(v.size / 1024).toFixed(1)} KB</Typography>
                  {v.is_latest ? <Chip size="small" color="success" label="Current" /> : <Button size="small" onClick={() => loadVersion(v)}>Load into the editor</Button>}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}

      {fleet && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Roll out to existing servers</Typography>
            <Typography variant="body2" color="text.secondary">Servers tagged {group.tag}. Go in order: Development, then Test, then Production.</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {fleet.waves.map((w) => {
              const c = fleet.wave_counts[w];
              return (
                <Paper key={w} variant="outlined" sx={{ p: 1.5, minWidth: 210 }}>
                  <Typography variant="subtitle1">{w}</Typography>
                  <Typography variant="body2" color="text.secondary">{c.matches} of {c.total} on the default</Typography>
                  <Button size="small" variant="contained" sx={{ mt: 1 }} disabled={!c.needs || changed} onClick={() => setRolling({ wave: w, counts: c })}>Roll out to {c.needs}</Button>
                </Paper>
              );
            })}
          </Box>
          {changed && <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>Save or discard your edit first: a rollout always uses the saved default.</Typography>}
          {fleet.not_windows > 0 && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>{fleet.not_windows} Linux server(s) with this tag are not managed here.</Typography>}
        </Paper>
      )}

      {fleet && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Servers ({fleet.servers.length})</Typography>
            {['matches', 'differs', 'not checked', 'unreachable', 'error', 'not installed'].filter((k) => tally[k]).map((k) => <Chip key={k} size="small" color={STATUS[k]} variant={k === 'matches' ? 'filled' : 'outlined'} label={`${tally[k]} ${k}`} onClick={() => setStatusFilter(statusFilter === k ? '' : k)} />)}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
            <TextField size="small" label="Search by name" value={query} onChange={(e) => setQuery(e.target.value)} />
            <TextField select size="small" label="Environment" value={envFilter} onChange={(e) => setEnvFilter(e.target.value)} sx={{ minWidth: 170 }} slotProps={NATIVE}>
              <option value="">All</option>
              {fleet.waves.map((w) => <option key={w} value={w}>{w}</option>)}
            </TextField>
            <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={runCheck} disabled={checking}>{checking ? 'Checking...' : `Check ${envFilter || 'all'} servers`}</Button>
            <Button startIcon={<RefreshIcon />} onClick={() => loadFleet(true)}>Refresh the list</Button>
          </Box>
          {checking && <LinearProgress sx={{ mb: 1 }} />}
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Server</TableCell><TableCell>Environment</TableCell><TableCell>Region</TableCell><TableCell>Config</TableCell><TableCell>Telegraf</TableCell><TableCell>Service</TableCell><TableCell>Last checked</TableCell></TableRow></TableHead>
              <TableBody>
                {rows.slice(page * PER, page * PER + PER).map((s) => (
                  <TableRow key={s.instance_id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{s.name}</TableCell><TableCell>{s.environment}</TableCell><TableCell>{s.region}</TableCell>
                    <TableCell><Chip size="small" color={STATUS[s.status]} variant={s.status === 'matches' ? 'filled' : 'outlined'} label={s.status} />{s.error && <Typography variant="caption" color="error" display="block">{s.error}</Typography>}</TableCell>
                    <TableCell>{s.telegraf_version ? s.telegraf_version.replace(/ \(git.*/, '') : '-'}</TableCell><TableCell>{s.service_state || '-'}</TableCell><TableCell>{fmt(s.checked_at)}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No server matches.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={rows.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={PER} rowsPerPageOptions={[PER]} />
        </Paper>
      )}

      <History group={group} refreshKey={refreshKey} />
      {reviewing && <ReviewDialog group={group} master={master} text={text} onClose={() => setReviewing(false)} onSaved={bump} />}
      {rolling && <RolloutDialog group={group} wave={rolling.wave} counts={rolling.counts} masterSha={master.sha} onClose={() => { setRolling(null); loadFleet(); }} onDone={bump} />}
    </Box>
  );
}

function TelegrafFleet() {
  const { isAdmin, loading } = usePermissions();
  const [tab, setTab] = useState(0);
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (!isAdmin()) return <Alert severity="info">Only Cloud Services administrators can manage the Telegraf configs.</Alert>;
  return (
    <Box>
      <Typography variant="h4" gutterBottom>Telegraf Fleet</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The default Telegraf config that sends host metrics to Sumo Logic from the application servers and the database servers (found by their Sumo tag). Change the default here, check which servers match it, then roll it out in waves with a backup of every old file. The two monitor servers are managed on Monitor Servers.
      </Typography>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        {GROUPS.map((g) => <Tab key={g.key} label={`${g.label} (${g.tag})`} />)}
      </Tabs>
      <Group key={GROUPS[tab].key} group={GROUPS[tab]} />
    </Box>
  );
}

export default TelegrafFleet;
